import { useCallback } from "react";
import { TaskTypeSchema, type TaskType } from "@myapp/contract";
import { parseAsInteger, parseAsStringLiteral, useQueryStates } from "nuqs";

/**
 * URL state for the task upsert modal — the sibling of `useUpsertAppointmentParams`.
 *
 * Param names are prefixed (`upsert-task-*`) so a page can host this modal next to the task
 * *filters* or the appointment upsert modal without the keys colliding.
 *
 * `upsert-task-type` only seeds the create form: the type is picked in the "Neue Aufgabe" menu
 * before the modal exists, and it is a form field from then on. In edit mode it is ignored —
 * the type comes from the loaded task.
 */

const MODES = ["create", "edit"] as const;

export const useUpsertTaskSearchParams = () =>
  useQueryStates(
    {
      "upsert-task": parseAsStringLiteral(MODES),
      "upsert-task-id": parseAsInteger,
      "upsert-task-type": parseAsStringLiteral(TaskTypeSchema.options),
    },
    { history: "replace", shallow: true, clearOnDefault: true },
  );

export type UpsertTaskParams = ReturnType<typeof useUpsertTaskSearchParams>[0];

export function useUpsertTask() {
  const [params, setParams] = useUpsertTaskSearchParams();

  const openCreate = useCallback(
    (taskType?: TaskType) =>
      setParams({
        "upsert-task": "create",
        "upsert-task-id": null,
        "upsert-task-type": taskType ?? null,
      }),
    [setParams],
  );

  const openEdit = useCallback(
    (id: number) =>
      setParams({
        "upsert-task": "edit",
        "upsert-task-id": id,
        "upsert-task-type": null,
      }),
    [setParams],
  );

  const close = useCallback(
    () =>
      setParams({
        "upsert-task": null,
        "upsert-task-id": null,
        "upsert-task-type": null,
      }),
    [setParams],
  );

  return {
    /** Derived from the mode's presence — never a separate boolean param. */
    isOpen: params["upsert-task"] != null,
    mode: params["upsert-task"],
    taskId: params["upsert-task-id"],
    taskType: params["upsert-task-type"],
    openCreate,
    openEdit,
    close,
  };
}
